import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  RiCheckLine,
  RiCloseLine,
  RiUploadCloud2Line,
  RiDeleteBinLine,
  RiCheckboxCircleFill,
  RiImageLine,
  RiArrowDownSLine,
  RiLoader4Line,
  RiAlertLine,
  RiAddCircleLine,
} from '@remixicon/react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../../../../components/ui/Drawer';
import { formatPrice, trunc2 } from '@/utils';
import { usePlatformFeeRate } from '@/hooks/usePlatformFeeRate';
import { formatPlatformFeePercent } from '@/utils/platformFee';
import AmenitiesInput from '@/components/ui/AmenitiesInput';
import TabStrip from '@/components/ui/TabStrip';
import { getAircraftModelName, probeImageUrl, amenitiesFromStructured } from '@/features/tails/utils';
import AircraftImage from '@/features/tails/components/AircraftImage';
import { resolveAssetUrl } from '@/utils/urls';
import { getAircraftBundledCarouselUrls } from '@assets/images';

const MAX_PHOTOS = 10;
const MAX_FILE_MB = 10;
const DESCRIPTION_LIMIT = 500;

/**
 * Split the aircraft_amenities.additional_amenities string (BE stores as a
 * comma-separated text field) into a chip array.
 */
const splitAmenities = (val) => {
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val !== 'string' || !val) return [];
  return val.split(',').map((s) => s.trim()).filter(Boolean);
};

/**
 * Build the buffered custom_profile payload from the drawer form.
 * Returns:
 *   - a profile object shaped like the /custom-profile POST body plus
 *     an internal `_pendingUploads` sidecar carrying any newly-picked
 *     File objects (uploaded to S3 by useQuoteProcessor on Save Draft /
 *     Send / Submit for Approval / Update from the preview drawer). New
 *     photos land in `image_urls` as their blob previewUrl so every
 *     downstream card / preview renders them immediately.
 *   - `null` when every field matches the base aircraft (broker
 *     effectively reverted the profile) — the processor turns that
 *     into a DELETE.
 */
const buildProfileBuffer = ({
  baseAircraft,
  tailNumber,
  aircraftName,
  description,
  amenities,
  notes,
  photos,
}) => {
  const patch = {};
  const extraAttributes = {};

  // Base for the display-name comparison:
  //   1) profile.name (custom_profile — platform-catalog org override)
  //   2) aircraft.extra_attributes.name (owned-aircraft broker label)
  //   3) make_model / model / derived model name
  // Matches the read-side priority in getEffectiveAircraft so an
  // unchanged hydrated value doesn't get flagged as dirty.
  const baseName = getAircraftModelName(baseAircraft) === '-' ? '' : getAircraftModelName(baseAircraft);
  const profileName = baseAircraft?.custom_profile?.name;
  const aircraftExtrasName = baseAircraft?.extra_attributes?.name;
  const rawBaseName = profileName
    || aircraftExtrasName
    || baseAircraft?.make_model
    || baseAircraft?.model
    || baseName
    || '';
  const trimmedName = aircraftName.trim();
  if (trimmedName && trimmedName !== rawBaseName) patch.name = trimmedName;

  const baseDesc = baseAircraft?.custom_profile?.description
    || baseAircraft?.description
    || '';
  const trimmedDesc = description.trim();
  if (trimmedDesc !== baseDesc) patch.description = trimmedDesc || null;

  // Photos: existing rows contribute their persisted URL, new rows
  // contribute their blob previewUrl so cards render before S3 upload.
  const items = Array.isArray(photos) ? photos : [];
  const imageUrls = items
    .map((p) => (p.kind === 'existing' ? p.url : p.previewUrl))
    .filter(Boolean);
  const pendingUploads = items
    .filter((p) => p.kind === 'new')
    .map((p) => ({
      previewUrl: p.previewUrl,
      file: p.file,
      name: p.name,
      size: p.size,
    }));
  // Base for the image diff. For platform-catalog aircraft the org's
  // photos live under custom_profile.image_urls (the aircraft's own
  // image_urls belong to whoever owns the platform record). For owned
  // aircraft, the aircraft's own image_urls IS the persisted state.
  // Whichever exists first wins as the "compare against" set.
  const profileImageUrls = Array.isArray(baseAircraft?.custom_profile?.image_urls)
    ? baseAircraft.custom_profile.image_urls
    : null;
  const baseImageUrls = profileImageUrls
    || (Array.isArray(baseAircraft?.image_urls) ? baseAircraft.image_urls : []);
  const imagesChanged =
    pendingUploads.length > 0 ||
    imageUrls.length !== baseImageUrls.length ||
    imageUrls.some((u, i) => u !== baseImageUrls[i]);
  if (imagesChanged) {
    patch.image_urls = imageUrls;
    patch.banner_image_url = imageUrls[0] || null;
    if (pendingUploads.length > 0) patch._pendingUploads = pendingUploads;
    if (baseImageUrls.length > 0) {
      patch._originalImageUrls = baseImageUrls;
    }
  }

  // Amenity base — mirror the state-init priority in the drawer's open
  // effect so we compare against the SAME source the form was seeded
  // from. When the aircraft has a stored custom_profile with an amenities
  // list, that list is what the form shows on open; the platform-catalog
  // `aircraft_amenities` structured shape is the fallback. Comparing to
  // the structured shape here (as this branch used to) always produced
  // a false-positive diff when a custom_profile.extra_attributes.amenities
  // was set, forcing a POST /custom-profile on every quote save even
  // when the broker didn't touch the Aircraft Details tab.
  const profileAmenities = baseAircraft?.custom_profile?.extra_attributes?.amenities;
  const baseAmenities = Array.isArray(profileAmenities)
    ? profileAmenities
    : amenitiesFromStructured(baseAircraft?.aircraft_amenities);
  const currentAmenities = amenities.filter(Boolean);
  const amenitiesChanged =
    baseAmenities.length !== currentAmenities.length ||
    baseAmenities.some((a, i) => a !== currentAmenities[i]);
  if (amenitiesChanged) extraAttributes.amenities = currentAmenities;

  const baseNotes = baseAircraft?.custom_profile?.extra_attributes?.notes
    || baseAircraft?.extra_attributes?.notes
    || '';
  const trimmedNotes = notes.trim();
  if (trimmedNotes !== baseNotes) extraAttributes.notes = trimmedNotes;

  const baseTail = baseAircraft?.custom_profile?.tail_number
    || baseAircraft?.tail_number
    || '';
  const trimmedTail = tailNumber.trim();
  if (trimmedTail && trimmedTail !== baseTail) patch.tail_number = trimmedTail;

  if (Object.keys(extraAttributes).length > 0) patch.extra_attributes = extraAttributes;

  // Owned-aircraft used to stash `_aircraftOrgId` / `_aircraftExtraAttributes`
  // / `_hasAmenitiesRow` sidecars here so persistAircraftProfiles could
  // route to `PATCH /aircrafts/:id` + `PATCH /aircrafts/:id/amenities`
  // instead of the /custom-profile endpoint. That branch poisoned the
  // base aircraft for every other quote — removed. Every edit now flows
  // through `custom_profile`, keeping the underlying aircraft row (and
  // its amenities row) as canonical shared state.
  const meaningfulKeys = Object.keys(patch).filter(
    (k) => k !== '_pendingUploads' && k !== '_originalImageUrls',
  );
  return meaningfulKeys.length > 0 ? patch : null;
};

/**
 * EditPricingDrawer — two-tab drawer for editing an aircraft option's
 * per-quote overrides + pricing.
 *
 * Tab 1 (Aircraft Details): org-level `custom_profile` on the aircraft.
 *   Save BUFFERS the edit into formData.aircraftProfiles via
 *   onSaveAircraftProfile. Nothing hits S3 or the DB here — the actual
 *   POST /organizations/:orgId/aircrafts/:aircraftId/custom-profile
 *   (or DELETE, when the broker reverted to base) happens in
 *   useQuoteProcessor when the broker fires Save Draft / Send /
 *   Submit for Approval / Update from the preview drawer. Photo
 *   uploads are deferred to that flow too, so a broker who Applies
 *   photos but never saves the quote leaves nothing in S3.
 *
 * Tab 2 (Pricing Details): the existing per-option pricing form. Saved
 *   via the pre-existing `onSave` prop into the bidOption (buffered in
 *   formData; useQuoteProcessor persists it on quote save).
 */
const EditPricingDrawer = ({
  isOpen = false,
  onClose,
  aircraft,
  passengerCount = 6,
  flightType = 'one-way',
  flightsLength = 1,
  onSave,
  onSaveAircraftProfile,
  bufferedProfile,
  // Per-quote operator-name override — display-only, does NOT mutate
  // the operator record. Parent passes the currently buffered override
  // (or undefined) and receives edits via onSaveOperatorNameOverride.
  bufferedOperatorName,
  onSaveOperatorNameOverride,
}) => {
  const baseAircraft = aircraft?.aircraft || aircraft;
  const aircraftId = baseAircraft?.aircraft_id;
  // Prefer the drawer-local buffered edit (broker mid-edit, hasn't
  // saved the quote yet) over the persisted profile on the aircraft
  // record. bufferedProfile === null means "revert to base"; undefined
  // means "no buffer entry — fall through to persisted".
  const effectiveProfile =
    bufferedProfile !== undefined
      ? bufferedProfile
      : baseAircraft?.custom_profile || null;
  const existingExtras =
    (effectiveProfile?.extra_attributes && typeof effectiveProfile.extra_attributes === 'object')
      ? effectiveProfile.extra_attributes
      : {};

  // ── Tabs ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('aircraft');

  // ── Aircraft details form state ──────────────────────────────────────────
  const [tailNumber, setTailNumber] = useState('');
  const [aircraftName, setAircraftName] = useState('');
  const [operatorNameDraft, setOperatorNameDraft] = useState('');
  const [description, setDescription] = useState('');
  const [amenities, setAmenities] = useState([]);
  const [notes, setNotes] = useState('');
  // Unified photo list — mix of already-saved S3 URLs and freshly-picked
  // File objects. Broker can drag-reorder the list; on save we walk it in
  // display order to build image_urls[], so the persisted order matches
  // exactly what they saw.
  //   Existing item: { id, kind: 'existing', url }
  //   New item:      { id, kind: 'new', file, previewUrl, name, size }
  const [photos, setPhotos] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  // Per-photo probe status: 'checking' | 'ok' | 'broken'. Existing S3
  // URLs get probed on drawer open so the broker sees a ⚠️ badge on
  // rows whose image can't actually load — new File-backed photos and
  // blob: previews are always OK (they're local). Display-only state,
  // never persisted.
  const [probeResults, setProbeResults] = useState({});
  // Next-button confirm modal — raised when the broker tries to advance
  // to Pricing Details while any existing photo row is broken.
  const [showBrokenConfirm, setShowBrokenConfirm] = useState(false);
  const nextPhotoIdRef = useRef(0);
  const makePhotoId = () => `p${++nextPhotoIdRef.current}`;
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  // Canonical operator name pulled off the base record — never mutated
  // by the broker's per-quote edit; used as the revert target below.
  const baseOperatorName = aircraft?.operator?.name || baseAircraft?.operator?.name || '';

  // Pristine base values — the "revert target" for each override-able
  // field. Every X-button / Reset action snaps its field back to these,
  // and buildProfileBuffer diffs against them at save time.
  const baseValues = useMemo(() => {
    const modelName = getAircraftModelName(baseAircraft) === '-' ? '' : getAircraftModelName(baseAircraft);
    return {
      tail: baseAircraft?.tail_number || '',
      // Revert target for the display name — mirrors the read-side
      // priority chain in getEffectiveAircraft so an owned aircraft
      // reverts to its extra_attributes.name (broker's persisted label)
      // rather than jumping back to the raw make_model.
      name: baseAircraft?.extra_attributes?.name
        || baseAircraft?.make_model
        || baseAircraft?.model
        || modelName
        || '',
      description: baseAircraft?.description || '',
      // Amenities revert target reconstructs the chip list from
      // structured has_* bools + additional_amenities. Same shared
      // helper the persist branch uses to round-trip cleanly.
      amenities: amenitiesFromStructured(baseAircraft?.aircraft_amenities),
      images: Array.isArray(baseAircraft?.image_urls) ? baseAircraft.image_urls : [],
    };
  }, [baseAircraft]);

  const arraysEqual = (a, b) =>
    a.length === b.length && a.every((v, i) => v === b[i]);
  const photosMatchBase = useMemo(() => {
    if (photos.some((p) => p.kind === 'new')) return false;
    const urls = photos.map((p) => p.url);
    return arraysEqual(urls, baseValues.images);
  }, [photos, baseValues.images]);

  // Bundled fallback images for the base aircraft's make_model. We use
  // getAircraftBundledCarouselUrls (NOT getAircraftCarouselImages) so the
  // returned URLs are absolute `${origin}/images/aircraft/{dir}/{file}` —
  // stable public-folder paths. formatImagUrl passes absolute URLs through
  // unchanged, so once persisted into image_urls[] these don't get the
  // REACT_APP_IMAGE_BASE_URL (S3 CDN) prefix that broke the webpack
  // module URLs (relative /static/media/foo.abc.jpg → 404 on the S3 host).
  // Empty when there is no bundle for this model.
  const bundledFallbacks = useMemo(() => {
    const makeModel = baseAircraft?.aircraft_make_model?.make_model || baseAircraft?.make_model;
    if (!makeModel) return [];
    return getAircraftBundledCarouselUrls(makeModel) || [];
  }, [baseAircraft]);
  const hasBundledFallback = bundledFallbacks.length > 0;

  // Rows that are currently rendering broken (probe returned false).
  // File-backed/blob rows are inherently OK (they're local previews);
  // we only ever probe URLs on `existing` rows. A row still 'checking'
  // is treated as OK until the probe resolves — the Next-button warning
  // uses this same set, so slow probes never block navigation.
  const brokenPhotoIds = useMemo(() => {
    return new Set(
      photos
        .filter((p) => p.kind === 'existing' && probeResults[p.id] === 'broken')
        .map((p) => p.id),
    );
  }, [photos, probeResults]);
  const hasBrokenPhotos = brokenPhotoIds.size > 0;
  const allPhotosBroken = photos.length > 0 && brokenPhotoIds.size === photos.length;

  // Probe every existing URL when the drawer opens / aircraft changes /
  // the broker adds or removes rows. New rows come in as 'ok' immediately
  // (blob preview always works). Existing rows go through 'checking' →
  // 'ok'/'broken' as the HTTP HEAD-ish probe resolves. Stale entries for
  // removed photos are dropped to keep the map tidy.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    // Snapshot: keep 'ok'/'broken' verdicts we already have; mark new
    // existing rows 'checking'; new rows are 'ok' by definition.
    setProbeResults((prev) => {
      const next = {};
      for (const p of photos) {
        if (p.kind === 'new') {
          next[p.id] = 'ok';
        } else if (prev[p.id] === 'ok' || prev[p.id] === 'broken') {
          next[p.id] = prev[p.id];
        } else {
          next[p.id] = 'checking';
        }
      }
      return next;
    });

    // Kick off probes for anything still 'checking' after the snapshot.
    photos.forEach((p) => {
      if (p.kind !== 'existing') return;
      // Skip if we already know its verdict.
      const knownVerdict = probeResults[p.id];
      if (knownVerdict === 'ok' || knownVerdict === 'broken') return;
      const url = resolveAssetUrl(p.url);
      probeImageUrl(url).then((ok) => {
        if (cancelled) return;
        setProbeResults((prev) => ({ ...prev, [p.id]: ok ? 'ok' : 'broken' }));
      });
    });

    return () => { cancelled = true; };
    // Depend only on the photo id set — probeResults changing shouldn't
    // retrigger probes, and the snapshot logic above merges old + new state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, photos.map((p) => `${p.kind}:${p.id}:${p.url || p.previewUrl}`).join('|')]);

  // Hydrate the form when the drawer opens or the aircraft changes.
  useEffect(() => {
    if (!isOpen || !baseAircraft) return;
    setActiveTab('aircraft');

    setTailNumber(effectiveProfile?.tail_number ?? baseAircraft.tail_number ?? '');
    // Operator name — seed from the currently buffered override for
    // this aircraft, otherwise the base operator record's name.
    // Empty-string override means "cleared, use base"; only a truthy
    // string overrides the base.
    setOperatorNameDraft(
      (typeof bufferedOperatorName === 'string' && bufferedOperatorName.trim())
        ? bufferedOperatorName
        : baseOperatorName,
    );
    // Display name — same priority chain as getEffectiveAircraft:
    //   custom_profile.name (platform-catalog org override)
    //   aircraft.extra_attributes.name (owned-aircraft broker label)
    //   make_model / model / derived model name
    setAircraftName(
      effectiveProfile?.name
        ?? baseAircraft.extra_attributes?.name
        ?? baseAircraft.make_model
        ?? baseAircraft.model
        ?? (getAircraftModelName(baseAircraft) === '-' ? '' : getAircraftModelName(baseAircraft)),
    );
    setDescription(effectiveProfile?.description ?? baseAircraft.description ?? '');
    // Amenities — union of structured booleans + additional_amenities
    // via the shared lexicon helper. On next save, the same lexicon
    // splits chips back into has_* bools for owned-aircraft PATCH.
    const amenityString =
      Array.isArray(existingExtras.amenities)
        ? existingExtras.amenities
        : amenitiesFromStructured(baseAircraft.aircraft_amenities);
    setAmenities(amenityString);
    setNotes(
      typeof existingExtras.notes === 'string' && existingExtras.notes
        ? existingExtras.notes
        : (baseAircraft.extra_attributes?.notes || ''),
    );

    // Reconstruct the unified photos list. When the buffer already
    // carries `_pendingUploads`, we walk image_urls and pair each blob
    // URL with its File so drag/reorder/remove keeps working across
    // drawer reopens without re-uploading. Blob URLs remain valid as
    // long as the File object stays referenced (formData holds it).
    const initialUrls =
      Array.isArray(effectiveProfile?.image_urls)
        ? effectiveProfile.image_urls
        : Array.isArray(baseAircraft.image_urls)
          ? baseAircraft.image_urls
          : [];
    const pendingByPreview = new Map();
    (effectiveProfile?._pendingUploads || []).forEach((p) => {
      if (p?.previewUrl) pendingByPreview.set(p.previewUrl, p);
    });
    // Revoke any DRAWER-LOCAL blob URLs from a prior open (only for
    // File-backed photos the broker picked but never Applied — Applied
    // photos live in formData and their blob URL must survive).
    setPhotos((prev) => {
      const kept = new Set(pendingByPreview.keys());
      prev.forEach((p) => {
        if (p.kind === 'new' && p.previewUrl && !kept.has(p.previewUrl)) {
          URL.revokeObjectURL(p.previewUrl);
        }
      });
      return initialUrls.map((url) => {
        const pending = pendingByPreview.get(url);
        if (pending) {
          return {
            id: makePhotoId(),
            kind: 'new',
            file: pending.file,
            previewUrl: pending.previewUrl,
            name: pending.name,
            size: pending.size,
          };
        }
        return { id: makePhotoId(), kind: 'existing', url };
      });
    });
    setDragIndex(null);
    setDragOverIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, aircraftId]);

  // ── Pricing tab state (unchanged from previous version) ──────────────────
  const BROKER_MARGIN_RATE = 0.15;
  const FET_RATE = 0.075;
  // International Facilities Tax — $22.90 per passenger. Applied only when
  // the trip is flagged International. FET + Segment Tax are Domestic-only.
  const INTL_FACILITIES_TAX_PER_PAX = 22.90;
  // Bracket-aware resolver — every fee compute in this drawer runs
  // against THIS option's subtotal so a tiered-bracket org picks the
  // right rate as the broker edits operator_cost / broker_markup.
  const { resolveForSubtotal } = usePlatformFeeRate();
  const CREDIT_CARD_FEE_RATE = 0.03;

  const legMultiplier = flightType === 'round-trip' ? 2 : flightType === 'multi-leg' ? flightsLength : 1;
  const segmentFeeLabel = flightType === 'round-trip' ? '(Round trip)' : flightType === 'multi-leg' ? `(${flightsLength} legs)` : '(One-way)';

  const [operatorCost, setOperatorCost] = useState('');
  const [brokerMargin, setBrokerMargin] = useState('');
  const [isDomestic, setIsDomestic] = useState(true);
  const [isInternational, setIsInternational] = useState(false);
  // Live refs mirroring the trip-type state — used inside the checkbox
  // onClick / onChange guards so rapid consecutive clicks always read
  // the latest values instead of a stale closure captured on a prior
  // render (which could otherwise fire the "at least one" guard against
  // the previous state and block a legitimate uncheck).
  // Live refs mirroring the trip-type state so the checkbox onChange
  // guards read the latest values instead of a stale closure captured
  // on a prior render.
  const isDomesticRef = useRef(true);
  const isInternationalRef = useRef(false);
  useEffect(() => { isDomesticRef.current = isDomestic; }, [isDomestic]);
  useEffect(() => { isInternationalRef.current = isInternational; }, [isInternational]);
  const [federalExciseTax, setFederalExciseTax] = useState('');
  const [paxCount, setPaxCount] = useState('6');
  const [feePerLeg, setFeePerLeg] = useState('5.20');
  // Editable International Facilities Tax rate — defaults to the constant
  // but broker can override per quote (same UX as fee-per-leg for the
  // segment fee).
  const [intlFeePerPax, setIntlFeePerPax] = useState(String(INTL_FACILITIES_TAX_PER_PAX));
  // Preserve-until-edited: when an option already has persisted PF/CC/ST/final
  // (old quotes priced under the previous formula, or fresh quotes buffered by
  // AircraftCard), we surface those values verbatim until the broker actually
  // touches a pricing input. Any edit flips `pricingDirty` and the display +
  // save switch to live-computed values under the current formula.
  const [pricingDirty, setPricingDirty] = useState(false);
  const [preservedPricing, setPreservedPricing] = useState(null);
  // Track which option's trip flags have been seeded so we only initialize
  // them once per (option, drawer-open) session. Prevents the load effect
  // from clobbering user checkbox toggles when it re-runs due to unrelated
  // dep changes (passengerCount, legMultiplier, resolveForSubtotal, etc).
  const tripFlagsInitializedForRef = useRef(null);

  useEffect(() => {
    if (!aircraft || !isOpen) {
      // Reset the init-guard on close so re-opening loads flags fresh.
      tripFlagsInitializedForRef.current = null;
      return;
    }
    // Distinguish "value was never saved" from "broker saved 0".
    // The old `> 0` guard treated both as missing and fell back to
    // total_price — which meant clicking Save with operator_cost=0
    // and any non-zero segment fee / trust-jet fee wrote $0 as
    // operator_cost but then re-hydrated the total on reopen. Same
    // class of bug on broker_markup — a saved 0 was replaced by the
    // computed 15% fallback.
    const hasSavedField = (v) => v !== null && v !== undefined && v !== '';
    const savedOpCost = hasSavedField(aircraft.operator_cost)
      ? parseFloat(aircraft.operator_cost)
      : NaN;
    const opCost = Number.isFinite(savedOpCost)
      ? savedOpCost
      : (parseFloat(aircraft.total_price) || 0);
    const savedBrokerMkup = hasSavedField(aircraft.broker_markup)
      ? parseFloat(aircraft.broker_markup)
      : NaN;
    const brokerMkup = Number.isFinite(savedBrokerMkup)
      ? savedBrokerMkup
      : trunc2(opCost * BROKER_MARGIN_RATE);
    setOperatorCost(opCost.toString());
    setBrokerMargin(brokerMkup.toString());
    const savedFet = parseFloat(aircraft.federal_excise_tax);
    if (!isNaN(savedFet) && savedFet > 0) {
      setFederalExciseTax(savedFet.toString());
    } else {
      const taxableBase = opCost + brokerMkup;
      setFederalExciseTax(trunc2(taxableBase * FET_RATE).toString());
    }
    const savedPaxCount = aircraft.segment_pax_count;
    const resolvedPax = (savedPaxCount !== undefined && savedPaxCount !== null)
      ? parseInt(savedPaxCount, 10) || passengerCount
      : passengerCount;
    setPaxCount(resolvedPax.toString());

    // Fee per leg — prefer the persisted rate. When the raw rate isn't
    // stored on the option (segment_fee_per_leg is dropped by BE on save),
    // back-solve it from the persisted segment_tax so the "Pax × Fee =
    // Total" row visually matches the persisted total. Falls back to the
    // $5.20 constant only when nothing has been priced yet.
    const savedFeePerLeg = parseFloat(aircraft.segment_fee_per_leg);
    const savedSegmentTax = parseFloat(aircraft.segment_tax);
    let resolvedFeePerLeg;
    if (!isNaN(savedFeePerLeg) && savedFeePerLeg > 0) {
      resolvedFeePerLeg = savedFeePerLeg;
    } else if (!isNaN(savedSegmentTax) && savedSegmentTax > 0 && resolvedPax > 0 && legMultiplier > 0) {
      resolvedFeePerLeg = trunc2(savedSegmentTax / (resolvedPax * legMultiplier));
    } else {
      resolvedFeePerLeg = 5.20;
    }
    setFeePerLeg(resolvedFeePerLeg.toString());

    // Same treatment for International Facilities Tax — back-solve the per-
    // pax rate from persisted international_tax when the raw rate isn't
    // stored. Bid_option and quote columns share the name `international_tax`.
    const savedIntlFeePerPax = parseFloat(aircraft.international_facilities_fee_per_pax);
    const savedIntlTax = parseFloat(aircraft.international_tax);
    let resolvedIntlFeePerPax;
    if (!isNaN(savedIntlFeePerPax) && savedIntlFeePerPax > 0) {
      resolvedIntlFeePerPax = savedIntlFeePerPax;
    } else if (!isNaN(savedIntlTax) && savedIntlTax > 0 && resolvedPax > 0) {
      resolvedIntlFeePerPax = trunc2(savedIntlTax / resolvedPax);
    } else {
      resolvedIntlFeePerPax = INTL_FACILITIES_TAX_PER_PAX;
    }
    setIntlFeePerPax(resolvedIntlFeePerPax.toString());

    // Trip-type flags — default to Domestic-only for legacy quotes that
    // predate the international-tax feature. Persisted values (boolean or
    // string) win when present.
    //
    // Only seed the flags on a fresh option (or first mount for the
    // current option). If this effect re-runs for the SAME option — say
    // because the parent handed down a new `passengerCount` — we skip so
    // the broker's checkbox toggles aren't reset back to the persisted
    // values behind their back.
    const currentOptionKey = aircraft.bid_option_id || aircraft.aircraft_id || '';
    if (tripFlagsInitializedForRef.current !== currentOptionKey) {
      tripFlagsInitializedForRef.current = currentOptionKey;
      const savedIsDomestic = aircraft.is_domestic;
      const savedIsIntl = aircraft.is_international;
      setIsDomestic(savedIsDomestic === undefined || savedIsDomestic === null ? true : !!savedIsDomestic);
      setIsInternational(!!savedIsIntl);
    }

    // Snapshot the persisted derived values so the display can honor them
    // until the broker edits an input. Requires all three so we don't
    // accidentally surface a partial (half-migrated) row.
    const persistedPF = parseFloat(aircraft.platform_fee);
    const persistedCC = parseFloat(aircraft.credit_card_fee);
    const persistedFinal = parseFloat(aircraft.final_price);
    const persistedST = parseFloat(aircraft.segment_tax);
    const persistedIntl = parseFloat(aircraft.international_tax);
    // CC fee is derivable (`totalBeforeCCFee * CREDIT_CARD_FEE_RATE`) and
    // is no longer persisted on bid_option — payment recomputes it live at
    // checkout, and the drawer live-computes it here. Only PF + Final need
    // to be persisted for the preserve-until-edited snapshot; CC falls
    // through to the live formula whether or not the row carries it.
    const hasPersistedPricing =
      Number.isFinite(persistedPF) && persistedPF > 0 &&
      Number.isFinite(persistedFinal) && persistedFinal > 0;
    setPreservedPricing(hasPersistedPricing ? {
      platformFee: persistedPF,
      creditCardFee: Number.isFinite(persistedCC) && persistedCC > 0 ? persistedCC : null,
      finalPrice: persistedFinal,
      segmentTax: Number.isFinite(persistedST) && persistedST >= 0 ? persistedST : null,
      internationalFacilitiesTax: Number.isFinite(persistedIntl) && persistedIntl >= 0 ? persistedIntl : null,
    } : null);
    setPricingDirty(false);
    // Depend on the stable option identifier rather than the whole
    // `aircraft` reference. The parent re-creates the aircraft object on
    // every re-render, which would otherwise re-run this init effect and
    // reset user-toggled state (Domestic/International checkboxes,
    // manually-typed FET, etc.) back to persisted values — visible as
    // click-then-snap-back and unexpected trip-type swaps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraft?.bid_option_id, aircraft?.aircraft_id, isOpen, passengerCount, legMultiplier, resolveForSubtotal]);

  const opCostNum = parseFloat(operatorCost) || 0;
  const brokerMarginNum = parseFloat(brokerMargin) || 0;
  const subtotalCost = trunc2(opCostNum + brokerMarginNum);
  const fetNum = parseFloat(federalExciseTax) || 0;
  const paxNum = parseInt(paxCount, 10) || 0;
  const feePerLegNum = parseFloat(feePerLeg) || 0;
  // Preserve-until-edited: honor the snapshotted PF/CC/ST/final for old
  // quotes until the broker touches an input. Once dirty, every derivation
  // falls through to the live formula so the cascade stays consistent.
  const usePersisted = !pricingDirty && preservedPricing !== null;

  // Segment fee is Domestic-only. Keep an always-visible "computed" value
  // for the pax × fee = total row in the UI so brokers still see the math
  // even when it isn't being applied, and a separate `totalSegmentFee`
  // which is what actually flows into the totals.
  const computedSegmentFee = trunc2(paxNum * feePerLegNum * legMultiplier);
  const persistedSegmentFee = usePersisted && preservedPricing.segmentTax !== null
    ? preservedPricing.segmentTax
    : computedSegmentFee;
  const totalSegmentFee = isDomestic ? persistedSegmentFee : 0;

  // Federal Excise Tax is Domestic-only. `fetNum` stays available for the
  // editable input; `effectiveFET` is what applies to the totals.
  const effectiveFET = isDomestic ? fetNum : 0;

  // International Facilities Tax — $22.90 × pax. Only applied when
  // International is currently ticked; honors the persisted snapshot on
  // old quotes until the broker edits an input. The outer isInternational
  // gate mirrors how effectiveFET / totalSegmentFee are gated on
  // isDomestic — so unchecking International reliably zeroes the tax
  // even when a persisted value exists on the quote.
  const intlFeePerPaxNum = parseFloat(intlFeePerPax) || 0;
  const computedIntlFacilitiesTax = trunc2(paxNum * intlFeePerPaxNum);
  const persistedIntlTax = usePersisted && preservedPricing?.internationalFacilitiesTax !== null && preservedPricing?.internationalFacilitiesTax !== undefined
    ? preservedPricing.internationalFacilitiesTax
    : computedIntlFacilitiesTax;
  const internationalFacilitiesTax = isInternational ? persistedIntlTax : 0;

  // Total taxes that apply to this trip under the Domestic/International
  // matrix. Both flags off → no taxes; both on → domestic taxes + intl.
  const totalTaxes = effectiveFET + totalSegmentFee + internationalFacilitiesTax;

  // Resolve the effective TrustJet fee for THIS option's current subtotal
  // (updates live as the broker edits operator_cost / broker_markup and
  // the subtotal crosses a bracket boundary). Rate is applied to
  // OC + BM + all applicable taxes so PF stays consistent across trip
  // types (domestic-only, international-only, mixed).
  const { percent: platformFeePercentEffective, rate: platformFeeRateEffective } = resolveForSubtotal(subtotalCost);
  const trustJetFee = usePersisted
    ? preservedPricing.platformFee
    : trunc2((subtotalCost + totalTaxes) * platformFeeRateEffective);
  const totalBeforeCCFee = usePersisted
    ? preservedPricing.finalPrice
    : trunc2(subtotalCost + trustJetFee + totalTaxes);
  // CC fee is grossed up so the merchant nets the full base after the
  // 3% processor cut. Using `total * 0.03` under-recovers — on a $100
  // base the client pays $103 but the processor takes 3% of $103 =
  // $3.09, leaving the merchant $99.91. Using `total / 0.97` grosses
  // the surcharge to $3.09 so the merchant nets the full $100. Same
  // formula as CardInfoForm's checkout math (dev commit 1713b82).
  // Legacy rows that stored credit_card_fee still get honored via
  // preservedPricing when present.
  const creditCardFee = usePersisted && preservedPricing?.creditCardFee != null
    ? preservedPricing.creditCardFee
    : trunc2(totalBeforeCCFee * CREDIT_CARD_FEE_RATE);
  const marginPercentage = opCostNum > 0 ? trunc2((brokerMarginNum / opCostNum) * 100) : 15;

  // ── Aircraft Details validation (for Next button) ────────────────────────
  const canProceed = useMemo(() => {
    // At minimum: still have a tail number, an aircraft name, and at least
    // one photo (kept or newly picked). Everything else is optional.
    if (!tailNumber.trim()) return false;
    if (!aircraftName.trim()) return false;
    if (photos.length === 0) return false;
    return true;
  }, [tailNumber, aircraftName, photos]);

  // ── Photo picker ─────────────────────────────────────────────────────────
  const totalPhotoCount = photos.length;
  const canAddMorePhotos = totalPhotoCount < MAX_PHOTOS;

  const handlePickPhotos = (fileList) => {
    const raw = Array.from(fileList || []);
    if (raw.length === 0) return;
    const accepted = raw.filter((f) => {
      const okType = /^image\/(jpeg|jpg|png)$/i.test(f.type);
      const okSize = f.size <= MAX_FILE_MB * 1024 * 1024;
      if (!okType) toast.error(`${f.name}: JPEG/PNG only.`);
      else if (!okSize) toast.error(`${f.name}: exceeds ${MAX_FILE_MB} MB.`);
      return okType && okSize;
    });
    if (accepted.length === 0) return;
    const remaining = MAX_PHOTOS - totalPhotoCount;
    if (remaining <= 0) {
      toast.error(`Max ${MAX_PHOTOS} photos.`);
      return;
    }
    const toAdd = accepted.slice(0, remaining);
    if (accepted.length > remaining) toast.error(`Only ${remaining} photo(s) slot(s) left.`);
    setPhotos((prev) => [
      ...prev,
      ...toAdd.map((file) => ({
        id: makePhotoId(),
        kind: 'new',
        file,
        name: file.name,
        size: file.size,
        previewUrl: URL.createObjectURL(file),
      })),
    ]);
  };

  const handleRemovePhoto = (index) => {
    setPhotos((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.kind === 'new' && removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  // ── Bundled-fallback picker actions ─────────────────────────────────────
  // Append a single bundled image URL as an 'existing' row. Bundled URLs
  // are stable app-asset paths (served from /images/…), so persisting
  // them into image_urls[] works exactly like a real S3 URL — every
  // consumer already renders them.
  const handleAddFallback = (url) => {
    if (!url) return;
    if (totalPhotoCount >= MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos.`);
      return;
    }
    setPhotos((prev) => [
      ...prev,
      { id: makePhotoId(), kind: 'existing', url },
    ]);
  };
  // Nuclear option: only offered when every row is broken AND a bundled
  // set exists. Drops every broken row and appends the full bundled set
  // in the same order the fallback chain would use. Any 'new' rows or
  // 'ok' existing rows survive (the button is guarded so this only fires
  // when there aren't any of those anyway).
  const handleReplaceAllBrokenWithFallback = () => {
    if (bundledFallbacks.length === 0) return;
    const remaining = Math.max(0, MAX_PHOTOS - (photos.length - brokenPhotoIds.size));
    const toAppend = bundledFallbacks.slice(0, remaining);
    setPhotos((prev) => [
      ...prev.filter((p) => !brokenPhotoIds.has(p.id)),
      ...toAppend.map((url) => ({ id: makePhotoId(), kind: 'existing', url })),
    ]);
  };

  // ── Drag-and-drop reorder (HTML5 native, no library dep) ─────────────────
  // dragIndex = the item being dragged; dragOverIndex = current drop target.
  // We only reorder on drop (not live during dragover) so the row layout
  // stays stable while the user is aiming — matches Finder / Files apps.
  const handleDragStart = (index) => (e) => {
    setDragIndex(index);
    // Firefox needs dataTransfer to actually initiate a drag.
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(index)); } catch (_) {}
  };
  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    setDragOverIndex(index);
  };
  const handleDrop = (index) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    setPhotos((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // ── Save (fires from Pricing tab's Save button) ──────────────────────────
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!aircraft || isSaving) return;
    const optionId = aircraft.bid_option_id || aircraft.aircraft_id;
    setIsSaving(true);
    try {
      // 1) Buffer the aircraft profile edit into formData via the parent
      //    callback. NO API call, NO S3 upload — those happen in
      //    useQuoteProcessor when the broker fires Save Draft / Send /
      //    Submit for Approval / Update from the preview drawer. Passing
      //    a profile object REPLACES the buffer entry; passing `undefined`
      //    drops the entry (no-op at persist time).
      //
      //    The previous `hasPersisted ? null : undefined` branch here
      //    emitted a DELETE whenever the broker opened the drawer on an
      //    aircraft with a stored custom_profile and hit Save without
      //    making any change on the Aircraft Details tab — buildProfile-
      //    Buffer's `null` return means "state matches its seed", which
      //    when the seed IS the custom_profile is a no-op, not a revert.
      //    A genuine revert-to-base happens via the per-field Revert /
      //    X buttons, which push base values into state → buildProfile-
      //    Buffer returns a non-null patch that overwrites the profile
      //    with the base shape; no separate DELETE path is needed.
      const profileBuffer = buildProfileBuffer({
        baseAircraft,
        tailNumber,
        aircraftName,
        description,
        amenities,
        notes,
        photos,
      });
      if (aircraftId && onSaveAircraftProfile) {
        onSaveAircraftProfile(
          aircraftId,
          profileBuffer
            ? { ...profileBuffer, updated_at: new Date().toISOString() }
            : undefined,
        );
      }

      // 1b) Operator name override — buffered under a separate key
      //     (quote.files.operator_name_overrides) because operator
      //     identity is intentionally NOT part of custom_profile
      //     (see getEffectiveAircraft's comment: operator is contractual
      //     and stays locked to the base record). Pass empty string
      //     when the input equals the base name → parent clears the
      //     override for this aircraft.
      if (aircraftId && onSaveOperatorNameOverride) {
        const trimmed = (operatorNameDraft || '').trim();
        onSaveOperatorNameOverride(
          aircraftId,
          trimmed && trimmed !== baseOperatorName ? trimmed : '',
        );
      }

      // 2) Pricing patch — buffered in parent formData via `onSave`.
      // Persist the *effective* FET and Segment Tax (0 when the trip is
      // International-only) so downstream emails/reports read the applied
      // amounts, not the informational input values.
      const totalCost = totalBeforeCCFee;
      onSave?.(optionId, {
        total_price: totalCost,
        operator_cost: operatorCost,
        broker_markup: brokerMargin,
        federal_excise_tax: effectiveFET,
        platform_fee: trustJetFee,
        segment_tax: totalSegmentFee,
        segment_pax_count: paxCount,
        segment_fee_per_leg: feePerLeg,
        international_tax: internationalFacilitiesTax,
        international_facilities_fee_per_pax: intlFeePerPax,
        is_domestic: isDomestic,
        is_international: isInternational,
        credit_card_fee: creditCardFee,
        final_price: totalCost,
      });

      // "Applied" — not "Saved" — because nothing is persisted yet;
      // the preview drawer's Save Draft / Send / Submit for Approval
      // flow is where the API write happens.
      toast.success('Applied. Save from the quote preview to persist.');
      onClose?.();
    } catch (err) {
      console.error('Failed to save aircraft/pricing:', err);
      toast.error(err?.message || 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent side="right" className="w-full max-w-md flex flex-col p-0" hideCloseButton>
        <DrawerHeader className="sr-only">
          <DrawerTitle>Edit Pricing</DrawerTitle>
        </DrawerHeader>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Edit Aircraft</h2>
            <p className="text-gray-500 mt-0.5 text-sm">
              {getAircraftModelName(baseAircraft) !== '-'
                ? getAircraftModelName(baseAircraft)
                : baseAircraft?.tail_number || 'Aircraft'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <RiCloseLine className="w-4 h-4" />
          </button>
        </div>

        {/* Tab strip */}
        <TabStrip
          tabs={[
            { key: 'aircraft', label: 'Aircraft Details' },
            { key: 'pricing', label: 'Pricing Details' },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-4">
          {activeTab === 'aircraft' ? (
            /* ── AIRCRAFT DETAILS TAB ─────────────────────────────── */
            <div className="space-y-4">
              {/* Tail Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tail Number <span className="text-primary-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={tailNumber}
                    onChange={(e) => setTailNumber(e.target.value.toUpperCase().slice(0, 10))}
                    placeholder="N123B"
                    maxLength={10}
                    className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {tailNumber !== baseValues.tail && (
                    <button
                      type="button"
                      onClick={() => setTailNumber(baseValues.tail)}
                      title={`Revert to ${baseValues.tail || 'original'}`}
                      aria-label={`Revert to ${baseValues.tail || 'original'}`}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <RiCloseLine className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">Max 10 characters.</p>
              </div>

              {/* Aircraft Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Aircraft Name <span className="text-primary-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={aircraftName}
                    onChange={(e) => setAircraftName(e.target.value)}
                    placeholder="Enter aircraft name..."
                    className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {aircraftName !== baseValues.name && (
                    <button
                      type="button"
                      onClick={() => setAircraftName(baseValues.name)}
                      title={`Revert to ${baseValues.name || 'original'}`}
                      aria-label={`Revert to ${baseValues.name || 'original'}`}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <RiCloseLine className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Operator Name — per-quote override (display only).
                  The edit does NOT mutate the operator record; it's
                  stored on quote.files.operator_name_overrides keyed
                  by aircraft_id and read via getEffectiveOperatorName /
                  getOperatorWithLocation across quote-related surfaces.
                  Empty input clears the override → falls back to base. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Operator Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={operatorNameDraft}
                    onChange={(e) => setOperatorNameDraft(e.target.value)}
                    placeholder={baseOperatorName || 'Operator'}
                    className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {operatorNameDraft && operatorNameDraft.trim() !== baseOperatorName && (
                    <button
                      type="button"
                      onClick={() => setOperatorNameDraft(baseOperatorName)}
                      aria-label="Revert operator name to the base record"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    >
                      <RiCloseLine className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                <div className="relative">
                  <textarea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT))}
                    placeholder="Tell us more about the aircraft..."
                    className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  />
                  {description !== baseValues.description && (
                    <button
                      type="button"
                      onClick={() => setDescription(baseValues.description)}
                      title="Revert to original description"
                      aria-label="Revert to original description"
                      className="absolute right-2.5 top-2.5 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <RiCloseLine className="w-4 h-4" />
                    </button>
                  )}
                  <div className="absolute bottom-2 right-3 text-xs text-gray-400 pointer-events-none">
                    {description.length}/{DESCRIPTION_LIMIT}
                  </div>
                </div>
              </div>

              {/* Amenities — chip input */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">Amenities</label>
                  {!arraysEqual(amenities, baseValues.amenities) && (
                    <button
                      type="button"
                      onClick={() => setAmenities([...baseValues.amenities])}
                      className="inline-flex items-center gap-0.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      title="Revert to original amenities"
                    >
                      <RiCloseLine className="w-3.5 h-3.5" />
                      Revert
                    </button>
                  )}
                </div>
                <AmenitiesInput value={amenities} onChange={setAmenities} />
                <p className="text-xs text-gray-500 mt-1">
                  Type an amenity and press Enter or comma to add.
                </p>
              </div>

              {/* Aircraft Photo */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Aircraft Photo <span className="text-primary-500">*</span>
                  </label>
                  {!photosMatchBase && (
                    <button
                      type="button"
                      onClick={() => {
                        // Reset unified list back to the base image_urls.
                        // Any pending File uploads and their preview URLs are
                        // dropped here — release the object URLs first.
                        setPhotos((prev) => {
                          prev.forEach((p) => p.kind === 'new' && p.previewUrl && URL.revokeObjectURL(p.previewUrl));
                          return baseValues.images.map((url) => ({ id: makePhotoId(), kind: 'existing', url }));
                        });
                        setDragIndex(null);
                        setDragOverIndex(null);
                      }}
                      className="inline-flex items-center gap-0.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      title="Revert to original photos"
                    >
                      <RiCloseLine className="w-3.5 h-3.5" />
                      Revert
                    </button>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    handlePickPhotos(e.target.files);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                />
                <input
                  ref={addMoreInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    handlePickPhotos(e.target.files);
                    if (addMoreInputRef.current) addMoreInputRef.current.value = '';
                  }}
                />

                {totalPhotoCount === 0 ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border border-dashed border-gray-300 rounded-2xl px-4 py-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                  >
                    <div className="mx-auto w-10 h-10 flex items-center justify-center text-primary-500">
                      <RiUploadCloud2Line className="w-8 h-8" />
                    </div>
                    <div className="mt-2 text-sm font-medium text-gray-900">Choose files</div>
                    <div className="text-xs text-gray-500 mt-0.5">JPEG and PNG formats, up to {MAX_FILE_MB} MB.</div>
                    <button
                      type="button"
                      className="mt-4 px-4 py-2 border border-gray-200 rounded-full text-sm text-gray-800 hover:bg-gray-50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Browse Files
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Unified photo list — drag any row to reorder. Existing
                        S3 URLs and newly-picked files coexist in one list so
                        the broker can interleave them freely. Existing raw
                        image_urls entries are usually relative S3 keys
                        (photos/N123GT/0.jpg), so we resolve through
                        REACT_APP_IMAGE_BASE_URL and let AircraftImage apply
                        its make_model → TrustJetPlane fallback chain. */}
                    {photos.map((photo, i) => {
                      const isNew = photo.kind === 'new';
                      const src = isNew ? photo.previewUrl : resolveAssetUrl(photo.url);
                      const label = isNew
                        ? photo.name
                        : (decodeURIComponent(photo.url.split('/').pop().split('?')[0]) || `Photo ${i + 1}`);
                      const isDragging = dragIndex === i;
                      const isDropTarget = dragOverIndex === i && dragIndex !== null && dragIndex !== i;
                      const probeState = probeResults[photo.id];
                      const isBroken = !isNew && probeState === 'broken';
                      const isChecking = !isNew && probeState === 'checking';
                      return (
                        <div
                          key={photo.id}
                          draggable
                          onDragStart={handleDragStart(i)}
                          onDragOver={handleDragOver(i)}
                          onDrop={handleDrop(i)}
                          onDragEnd={handleDragEnd}
                          className={`flex items-center gap-3 border rounded-xl px-3 py-2.5 cursor-grab active:cursor-grabbing transition-all ${
                            isDragging
                              ? 'opacity-40 border-gray-200'
                              : isDropTarget
                                ? 'border-primary-500 bg-primary-50/40'
                                : isBroken
                                  ? 'border-amber-300 bg-amber-50/40'
                                  : 'border-gray-200'
                          }`}
                        >
                          {/* Grip / drag affordance — non-interactive icon;
                              the whole row is the drag handle. */}
                          <span className="shrink-0 text-gray-300 select-none leading-none" aria-hidden="true">⋮⋮</span>
                          <div className="relative flex-shrink-0">
                            {isNew ? (
                              <img
                                src={src}
                                alt=""
                                className="w-10 h-10 rounded-md object-cover"
                              />
                            ) : (
                              <AircraftImage
                                aircraft={baseAircraft}
                                src={src}
                                alt=""
                                className="w-10 h-10 rounded-md object-cover bg-gray-100"
                              />
                            )}
                            {/* ⚠️ overlay — anchored to the thumbnail so it
                                stays with the image even when the row wraps
                                on narrow drawers. Only shown once probing
                                has resolved to 'broken'. */}
                            {isBroken && (
                              <span
                                className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white ring-2 ring-white"
                                title="This image failed to load"
                                aria-label="Broken image"
                              >
                                <RiAlertLine className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{label}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {isNew ? (
                                <>
                                  {(photo.size / (1024 * 1024)).toFixed(1)} MB ·{' '}
                                  <span className="inline-flex items-center gap-0.5 text-amber-600">
                                    Ready to upload
                                  </span>
                                </>
                              ) : isChecking ? (
                                <span className="inline-flex items-center gap-1 text-gray-400">
                                  <RiLoader4Line className="w-3 h-3 animate-spin" /> Checking…
                                </span>
                              ) : isBroken ? (
                                <span className="inline-flex items-center gap-0.5 text-amber-600">
                                  <RiAlertLine className="w-3 h-3" /> Image failed to load
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 text-green-600">
                                  <RiCheckboxCircleFill className="w-3 h-3" /> Saved
                                </span>
                              )}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemovePhoto(i)}
                            className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                            aria-label="Remove photo"
                          >
                            <RiDeleteBinLine className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                    {canAddMorePhotos && (
                      <button
                        type="button"
                        onClick={() => addMoreInputRef.current?.click()}
                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-full transition-colors"
                      >
                        <RiImageLine className="w-4 h-4" />
                        Add more images
                      </button>
                    )}

                    {/* Fallback picker — only shown when at least one row
                        is broken AND a bundled set exists for this
                        aircraft's make_model. Two paths for the broker:
                        (a) per-thumb Add for surgical selection, or
                        (b) one-click Replace All when EVERY current row
                        is broken (nuclear option — drops broken rows and
                        appends the full bundled set in fallback-chain
                        order). */}
                    {hasBrokenPhotos && hasBundledFallback && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-gray-700">
                            Suggested photos for {baseValues.name || 'this aircraft'}
                          </p>
                          {allPhotosBroken && (
                            <button
                              type="button"
                              onClick={handleReplaceAllBrokenWithFallback}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                              title="Drop broken images and use the bundled set"
                            >
                              Replace all with fallback
                            </button>
                          )}
                        </div>
                        <div className="flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                          {bundledFallbacks.map((url, i) => (
                            <button
                              key={`fallback-${i}-${url}`}
                              type="button"
                              onClick={() => handleAddFallback(url)}
                              disabled={!canAddMorePhotos}
                              className="group relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:border-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Add to photos"
                              aria-label="Add bundled fallback photo"
                            >
                              <img
                                src={url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                              <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <RiAddCircleLine className="w-6 h-6 text-white" />
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bottom note — always shown when any row is broken.
                        Wording changes based on whether a bundled set
                        exists so the broker knows exactly what clients
                        will see if they leave the broken URLs in place.
                        The whole point: doing nothing is safe. */}
                    {hasBrokenPhotos && (
                      <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                        <RiAlertLine className="inline w-3.5 h-3.5 text-amber-500 -mt-0.5 mr-0.5" />
                        Some photos failed to load.{' '}
                        {hasBundledFallback ? (
                          <>
                            If you leave them, clients will see bundled fallback photos
                            for <span className="font-medium">{baseValues.name || 'this aircraft'}</span> automatically — no action required.
                          </>
                        ) : (
                          <>
                            If you leave them, clients will see a TrustJet placeholder — no action required.
                          </>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Notes (per-quote, shown to broker + client) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Notes <span className="text-gray-400 font-normal">(Visible to Client)</span>
                </label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
          ) : (
            /* ── PRICING DETAILS TAB (existing content) ───────────── */
            <div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Operator Cost</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="text"
                      value={operatorCost}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setOperatorCost(value);
                        const newOpCost = parseFloat(value) || 0;
                        const newBrokerMargin = trunc2(newOpCost * BROKER_MARGIN_RATE);
                        setBrokerMargin(newBrokerMargin.toString());
                        const taxableBase = newOpCost + newBrokerMargin;
                        setFederalExciseTax(trunc2(taxableBase * FET_RATE).toString());
                        setPricingDirty(true);
                      }}
                      className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    ${opCostNum > 0 ? trunc2(opCostNum / 6).toLocaleString() : '0'}/hr base rate
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brokerage Margin</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="text"
                      value={brokerMargin}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, '');
                        setBrokerMargin(value);
                        const taxableBase = opCostNum + (parseFloat(value) || 0);
                        setFederalExciseTax(trunc2(taxableBase * FET_RATE).toString());
                        setPricingDirty(true);
                      }}
                      className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{marginPercentage}% margin</p>
                </div>
              </div>

              <div className="flex justify-between items-center py-4 border-b border-gray-200">
                <span className="text-sm text-gray-700">Subtotal Cost</span>
                <span className="text-lg font-semibold text-gray-900">{formatPrice(subtotalCost)}</span>
              </div>
              <div className="flex justify-between items-center py-4 border-b border-gray-200">
                <span className="text-sm text-gray-600">TrustJet Fee ({formatPlatformFeePercent(platformFeePercentEffective)}%)</span>
                <span className="text-sm text-gray-900">{formatPrice(trustJetFee)}</span>
              </div>
              <div className="flex justify-between items-center py-4 border-b border-gray-200">
                <span className="text-sm text-gray-600">Credit Card Fee (3%)</span>
                <span className="text-sm text-gray-900">{formatPrice(creditCardFee)}</span>
              </div>
              {/* Trip type — both can be checked (Domestic + International
                  taxes both apply); at least one must stay checked (we
                  can't leave both off). Toggles are independent; the guard
                  only fires when trying to uncheck the only-active one. */}
              <div className="flex justify-between items-center py-4 border-b border-gray-200">
                <span className="text-sm text-gray-700">Domestic Trip</span>
                <input
                  type="checkbox"
                  checked={isDomestic}
                  onChange={(e) => {
                    const next = e.target.checked;
                    // Rule: at least one trip type must stay checked.
                    if (!next && !isInternationalRef.current) {
                      // Refuse the uncheck — put the DOM checkbox back
                      // so state and DOM stay in sync.
                      e.target.checked = true;
                      toast.error('At least one trip type must be selected.');
                      return;
                    }
                    setIsDomestic(next);
                    setPricingDirty(true);
                  }}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-between items-center py-4 border-b border-gray-200">
                <span className="text-sm text-gray-700">International Trip</span>
                <input
                  type="checkbox"
                  checked={isInternational}
                  onChange={(e) => {
                    const next = e.target.checked;
                    if (!next && !isDomesticRef.current) {
                      e.target.checked = true;
                      toast.error('At least one trip type must be selected.');
                      return;
                    }
                    setIsInternational(next);
                    setPricingDirty(true);
                  }}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </div>
              {/* International Facilities Tax — editable rate per pax, same
                  layout as the Segment Fee row: pax (readonly, from flight
                  details) × fee-per-pax (broker-editable, default $22.90)
                  = total (readonly). Shown iff International is checked. */}
              {isInternational && (
                <div className="py-4 border-b border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    International Facilities Tax
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="w-16">
                      <input
                        type="text"
                        value={paxCount}
                        readOnly
                        className="w-full pl-7 pr-3 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900"
                      />
                      <p className="text-xs text-gray-500 mt-1 text-center">Pax</p>
                    </div>
                    <span className="text-gray-500">x</span>
                    <div className="w-24">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="text"
                          value={intlFeePerPax}
                          onChange={(e) => {
                            setIntlFeePerPax(e.target.value.replace(/[^0-9.]/g, ''));
                            setPricingDirty(true);
                          }}
                          className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-center">Fee per pax</p>
                    </div>
                    <span className="text-gray-500">=</span>
                    <div className="flex-1">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="text"
                          value={persistedIntlTax.toFixed(2)}
                          readOnly
                          className="w-full pl-7 pr-3 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-center">Total facilities tax</p>
                    </div>
                  </div>
                </div>
              )}
              {/* Federal Excise Tax + Segment Fee are Domestic-only. The
                  whole section hides when International is the only
                  selected trip type — the values are already zeroed in
                  the derivations, so hiding here matches the totals. */}
              {isDomestic && (
                <>
                  <div className="py-4 border-b border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Federal Excise Tax</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="text"
                        value={federalExciseTax}
                        onChange={(e) => {
                          setFederalExciseTax(e.target.value.replace(/[^0-9.]/g, ''));
                          setPricingDirty(true);
                        }}
                        className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">7.5% subtotal cost</p>
                  </div>
                  <div className="py-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Segment Fee <span className="font-normal text-gray-500">{segmentFeeLabel}</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="w-16">
                        <input
                          type="text"
                          value={paxCount}
                          onChange={(e) => setPaxCount(e.target.value.replace(/[^0-9]/g, ''))}
                          readOnly
                          className="w-full pl-7 pr-3 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900"
                        />
                        <p className="text-xs text-gray-500 mt-1 text-center">Pax</p>
                      </div>
                      <span className="text-gray-500">x</span>
                      <div className="w-24">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="text"
                            value={feePerLeg}
                            onChange={(e) => {
                              setFeePerLeg(e.target.value.replace(/[^0-9.]/g, ''));
                              setPricingDirty(true);
                            }}
                            className="w-full pl-7 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center">Fee per leg</p>
                      </div>
                      <span className="text-gray-500">=</span>
                      <div className="flex-1">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="text"
                            value={persistedSegmentFee.toFixed(2)}
                            readOnly
                            className="w-full pl-7 pr-3 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center">Total segment fee</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer — flex-shrink-0 sibling of the scroll pane so it hugs the
            drawer bottom regardless of viewport height. */}
        <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200">
          {activeTab === 'aircraft' ? (
            <button
              type="button"
              onClick={() => {
                // Warn if any row is confirmed broken — probing might
                // still be in-flight, but we don't block on it here (a
                // slow probe shouldn't gate navigation). The confirm
                // wording is fallback-aware, same conditional as the
                // bottom note in the photos section.
                if (hasBrokenPhotos) {
                  setShowBrokenConfirm(true);
                  return;
                }
                setActiveTab('pricing');
              }}
              disabled={!canProceed}
              className={`w-full px-4 py-3 rounded-full text-sm font-medium transition-all flex items-center justify-center gap-2 focus:outline-none focus:ring-0 ${
                canProceed
                  ? 'bg-accent text-primary-500 hover:bg-accent'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next: Pricing Details
              <RiArrowDownSLine className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="w-full px-4 py-3 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-500 disabled:cursor-not-allowed text-white rounded-full transition-colors flex items-center justify-center gap-2 font-medium"
            >
              {isSaving ? <RiLoader4Line className="w-5 h-5 animate-spin" /> : <RiCheckLine className="w-5 h-5" />}
              {isSaving ? 'Saving...' : 'Save Pricing'}
            </button>
          )}
        </div>

        {/* Broken-photos confirm — raised when the broker tries to move
            to Pricing Details with unresolved broken URLs in the list.
            Purely a checkpoint; the broker can still Continue. Rendered
            inside DrawerContent so its z-index sits above the drawer
            surface without needing portal wiring. */}
        {showBrokenConfirm && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/40"
            role="dialog"
            aria-modal="true"
            aria-labelledby="broken-photos-confirm-title"
            onClick={() => setShowBrokenConfirm(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl w-[90%] max-w-sm p-5 mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <RiAlertLine className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 id="broken-photos-confirm-title" className="text-base font-semibold text-gray-900">
                    Some photos couldn&apos;t load
                  </h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {hasBundledFallback ? (
                  <>
                    Bundled photos for <span className="font-medium">{baseValues.name || 'this aircraft'}</span> will
                    be shown to clients in place of the broken images. Continue?
                  </>
                ) : (
                  <>
                    Clients will see a generic placeholder in place of the broken images. Continue?
                  </>
                )}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowBrokenConfirm(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Back to Photos
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowBrokenConfirm(false);
                    setActiveTab('pricing');
                  }}
                  className="flex-1 px-4 py-2.5 bg-accent text-primary-500 rounded-full text-sm font-medium hover:bg-accent transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default EditPricingDrawer;
